/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import {
  select,
  getContext,
  call,
  put,
  race,
  take,
  delay,
  all,
} from "typed-redux-saga";
import {
  selectStartAndEnd,
  selectIsRollingMode,
  selectLastFetchCompletedMachinePerformance,
} from "@/store/selectors";
import { CMSClientType } from "@/cms-api";
import {
  Alert,
  Machine,
  MachineState,
  MachineStateSummary,
} from "@centralwebteam/narwhal";
import { getGlobalConfigs } from "@/index";
import { selectMachines } from "@/store/filter/selectors";
import { rootActions, RootState } from "..";
import { toUTCISOString } from "../../modules/dateFormats/index";
import { getMachineJobAggregate, getMachineJobs } from "./jobAggregate.worker";
import { machinePerformanceActions } from "./actions";
import { chunk, groupBy, orderBy } from "lodash/fp";
import { createJobPresentation } from "@/presentation/Job";
import { AlertPresentation } from "@/presentation/Alert";
const MACHINE_CHUNK_AMOUNT = 10;

/**
 * Fetches data for filtered machines between the from/to dates.
 */
export function* fetchStaticData() {
  // Clear all existing data immediately when new date range is requested
  yield* put(machinePerformanceActions.allDataCleared());

  const config = yield* call(() => getGlobalConfigs());
  // we should set an idle timeout if the user is flicking between rolling modes
  yield* delay(config.userLoaderTimeInMS);

  yield* put(machinePerformanceActions.fetchingStarted());
  const [from, to] = yield* select(selectStartAndEnd);
  try {
    const machines = yield* select(selectMachines);
    const chunkedMachines = chunk(MACHINE_CHUNK_AMOUNT, machines);
    yield* all([
      ...chunkedMachines.map((chunk) =>
        all([
          call(fetchMachinesJobAggregates, chunk, from, to, "static"),
          call(fetchMachinesStateSummaries, chunk, from, to, "static"),
        ])
      ),
      call(fetchMachinesAlertCounts, machines, from, to, "static"),
    ]);
  } catch (error) {
    console.info(
      "something went wrong fetching static machine performance data",
      error
    );
  } finally {
    yield* put(
      machinePerformanceActions.fetchingFinished({
        to,
      })
    );
  }
}

// LIVE REFRESH

/**
 * Waits for conditions to begin delta loop.
 */
export function* liveRefreshWatcher() {
  const config = yield* call(() => getGlobalConfigs());

  while (true) {
    // Loop every 1 second
    yield* delay(1000);

    const isRollingMode = yield* select(selectIsRollingMode);
    if (!isRollingMode) {
      continue;
    }

    const isLiveRefreshModeOn = yield* select(
      (state: RootState) => state.global.isLiveRefreshModeOn
    );
    if (!isLiveRefreshModeOn) {
      continue;
    }

    const timeSinceLastFetchCompleted = yield* select(
      selectLastFetchCompletedMachinePerformance
    );
    if (timeSinceLastFetchCompleted) {
      const now = new Date();
      const timeSinceLastFetch =
        now.getTime() - timeSinceLastFetchCompleted.getTime();

      if (timeSinceLastFetch < config.refreshRateSlowInMs) {
        continue;
      }
    }

    const { cancel } = yield* race({
      fetchDelta: call(fetchDeltas),
      cancel: call(waitForLiveRefreshModeChanged),
    });
    if (cancel) {
      yield* put(machinePerformanceActions.fetchingCancelled());
    }
  }
}

function* waitForLiveRefreshModeChanged() {
  do {
    const isLiveRefreshModeOn = yield* select(
      (state: RootState) => state.global.isLiveRefreshModeOn
    );
    if (!isLiveRefreshModeOn) return true;
  } while (yield* take(rootActions.global.liveRefreshModeStateChanged));
}

/**
 * Fetches the deltas for datasets which need refreshing.
 */
function* fetchDeltas() {
  const now = toUTCISOString(new Date());
  try {
    const lastFetched = yield* select(
      (state) => state.machinePerformance.lastFetched
    );
    const isRollingMode = yield* select(selectIsRollingMode);
    const isLiveRefreshModeOn = yield* select(
      (state: RootState) => state.global.isLiveRefreshModeOn
    );
    // comply with types
    if (!lastFetched || !isRollingMode || !isLiveRefreshModeOn) {
      return;
    }
    yield* put(machinePerformanceActions.fetchingStarted());
    const machines = yield* select(selectMachines);
    const chunkedMachines = chunk(MACHINE_CHUNK_AMOUNT, machines);
    yield* all([
      ...chunkedMachines.map((chunk) =>
        all([
          call(fetchMachinesJobAggregates, chunk, lastFetched, now, "delta"),
          call(fetchMachinesStateSummaries, chunk, lastFetched, now, "delta"),
        ])
      ),
      call(fetchMachinesAlertCounts, machines, lastFetched, now, "delta"),
    ]);
    yield* put(
      machinePerformanceActions.fetchingFinished({
        to: now,
      })
    );
  } catch (error) {
    console.log(error);
  }
}

/**
 * Fetches job summaries and aggregates into summary (running etc.) for supplied machines.
 * Puts an action to the store.
 */
function* fetchMachinesJobAggregates(
  machines: Machine[],
  from: string,
  to: string,
  type: "static" | "delta"
) {
  const config = yield* call(() => getGlobalConfigs());
  const client: CMSClientType = yield getContext("client");
  const top = yield* select((state) => state.machinePerformance.top);
  const machineIds = machines.map((machine) => machine.id);
  const summaryStats = yield* call(
    (query) => client.jobs.tempRawSummary(query).promise,
    {
      machines: machineIds,
      from,
      to,
      take: config.maxTake,
    }
  );

  try {
    for (const machineId of machineIds) {
      const data = yield* call(
        getMachineJobAggregate,
        summaryStats[machineId]?.map((job) => createJobPresentation(job)) ?? []
      );
      const topJobStats = yield* call(
        getMachineJobs,
        summaryStats[machineId]?.map((job) => createJobPresentation(job)) ?? [],
        top
      );

      const currentJobInProgress = yield* select(
        (state: RootState) => state.machinePerformance.jobsInProgress[machineId]
      );
      const latestJobInProgress = summaryStats[machineId]?.find(
        (job) => job.status === "Started"
      );

      if (
        currentJobInProgress &&
        latestJobInProgress &&
        currentJobInProgress.id === latestJobInProgress?.id
      ) {
        // If a job is in progress then the API will return it in the delta call each time.
        // For example, refresh cycle 1 returns { 'MyJob1: status: Started' }, cycle 2 will return the same thing
        // and this continues until the job completes. Therefore we only want to post this data to the store
        // once. If the currentJob = latestJob, it means we've already posted this update to the store.
        // A machine can only have 1 in progress job at a time, so we don't need to worry about
        // a machine having multiple jobs in progress.
        summaryStats[machineId] = summaryStats[machineId]?.filter(
          (job) => job.id !== currentJobInProgress.id
        );
        // Exit the current loop iteration as there's nothing to do as the current job in progress
        // is the same as the latest job in progress.
        continue;
      }

      // If we have updated data for the job which was in progress
      if (
        summaryStats[machineId]?.some(
          (job) => job.id === currentJobInProgress?.id
        )
      ) {
        const currentJobSummary = summaryStats[machineId]?.find(
          (job) => job.id === currentJobInProgress?.id
        );
        if (
          currentJobSummary?.status === "Completed" ||
          currentJobSummary?.status === "NoJobEnd"
        ) {
          const matchingJobStats = topJobStats.find(
            (job) => job.name === currentJobInProgress?.name
          );
          // If we have reached this point it means that a job which was in progress has
          // ended. When a job is in progress it has an incomplete and no verdict state.
          // When the job ends it becomes completed and gains a verdict. We therefore
          // need to subtract 1 from the incomplete and no verdict counts. It should
          // also not effect the total as it is still the same job.
          if (matchingJobStats) {
            matchingJobStats.incomplete -= 1;
            matchingJobStats.noVerdict -= 1;
            matchingJobStats.total -= 1;
          }
        }
      }

      yield* put(
        rootActions.machinePerformance.jobInProgress({
          machineId,
          job: latestJobInProgress,
        })
      );

      if (type === "static") {
        const payload = {
          jobAggregate: data,
          topJobStats: topJobStats,
          from,
          to,
          machineId,
        };
        yield* put(rootActions.machinePerformance.jobAggregateFetched(payload));
      } else {
        const payload = {
          jobAggregate: data,
          from,
          to,
          machineId,
        };
        yield* put(
          rootActions.machinePerformance.jobAggregateDeltaFetched({
            ...payload,
            topJobStats: topJobStats,
          })
        );
      }
    }
  } catch (error) {
    console.log(error);
  }
}

/**
 * Fetches the state summaries for supplied machines.
 * Puts an action to the store.
 */
function* fetchMachinesStateSummaries(
  machines: Machine[],
  from: string,
  to: string,
  type: "static" | "delta"
) {
  const client: CMSClientType = yield getContext("client");
  let stateSummaries = [] as MachineStateSummary[];
  try {
    stateSummaries = yield* call(
      (query) => client.machines.stateSummary(query).promise,
      {
        machineIds: machines.map((machine) => machine.id),
        from,
        to,
      }
    );
    for (const machineStateSummary of stateSummaries) {
      const payload = {
        statuses: machineStateSummary.stateSeconds,
        machineId: machineStateSummary.machineId,
        from,
        to,
      };
      const existingData = yield* select(
        (state) => state.machinePerformance.previousMachineStatus
      );
      const existingMachineJobStatus = existingData.find(
        (data: any) => data.machineId === machineStateSummary.machineId
      );
      let updatedData = payload;
      if (existingMachineJobStatus) {
        const updatedStatusesValues = (
          previousData: [MachineState, number][],
          currentData: [MachineState, number][]
        ): [MachineState, number][] => {
          const result: Partial<Record<MachineState, number>> = {};

          previousData.forEach(([key, value]) => {
            result[key] = (result[key] || 0) + value;
          });

          currentData.forEach(([key, value]) => {
            result[key] = (result[key] || 0) + value;
          });

          return Object.entries(result) as [MachineState, number][];
        };

        updatedData = {
          ...updatedData,
          statuses: updatedStatusesValues(
            existingMachineJobStatus.statuses,
            payload.statuses
          ),
        };
      }

      if (type === "static") {
        yield* put(rootActions.machinePerformance.statusesFetched(payload));
      } else if (updatedData) {
        yield* put(
          rootActions.machinePerformance.statusesDeltaFetched(updatedData)
        );
      }
    }
  } catch (ex) {
    console.warn(ex);
  }
}

/**
 * Fetches and counts the error and warning alerts for supplied machines.
 * Puts an action to the store.
 */
function* fetchMachinesAlertCounts(
  machines: Machine[],
  from: string,
  to: string,
  type: "static" | "delta"
) {
  const config = yield* call(() => getGlobalConfigs());
  const client: CMSClientType = yield getContext("client");
  for (const machine of machines) {
    let alerts: Alert[];

    try {
      alerts = yield* call(
        (from, to, machineId, take) =>
          client.events.alerts.errors(from, to, machineId, take).promise,
        from,
        to,
        machine.id,
        config.maxTake
      );
    } catch (ex) {
      console.warn(ex);
      continue;
    }

    const allActiveAlerts = alerts.map((alert) => new AlertPresentation(alert));
    const groupErrorAlerts = groupBy((alert) => alert.name, allActiveAlerts);
    const machineAlerts = orderBy(
      ["start"],
      ["asc"],
      Object.keys(groupErrorAlerts).map((alertName) => {
        return {
          name: alertName,
          count: Object.values(groupErrorAlerts[alertName]).length,
        };
      })
    );
    const payload = {
      alertCount: allActiveAlerts.length,
      machineAlerts,
      from,
      to,
      machineId: machine.id,
    };
    const existingData = yield* select(
      (state) => state.machinePerformance.previousAlertDetails
    );
    const existingMachineAlertDetails = existingData.find(
      (data: any) => data.machineId === machine.id
    );
    let updatedData = payload;

    if (existingMachineAlertDetails?.machineAlerts?.length) {
      const updatedMachineAlerts =
        existingMachineAlertDetails.machineAlerts.map((alert: any) => ({
          ...alert,
        }));

      payload.machineAlerts.forEach((newAlert) => {
        const existingAlertIndex = updatedMachineAlerts.findIndex(
          (alert: { name: string }) => alert.name === newAlert.name
        );

        if (existingAlertIndex !== -1) {
          updatedMachineAlerts[existingAlertIndex] = {
            ...updatedMachineAlerts[existingAlertIndex],
            count:
              updatedMachineAlerts[existingAlertIndex].count + newAlert.count,
          };
        } else {
          updatedMachineAlerts.push(newAlert);
        }
      });

      const alertCount = updatedMachineAlerts.reduce(
        (total: number, alert: { count: number }) => total + alert.count,
        0
      );

      updatedData = {
        ...updatedData,
        machineAlerts: updatedMachineAlerts,
        alertCount: alertCount,
      };
    }

    yield* put(
      type === "static"
        ? rootActions.machinePerformance.alertCountsFetched(payload)
        : rootActions.machinePerformance.alertCountsDeltaFetched(updatedData)
    );
  }
}
